import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectEditFurnitureRows,
  selectEditUtilitiesRows,
  selectFurnitureCostToTravelerError,
} from 'store/redux-store/placement-mods/selectors';
import { GridCellParams } from '@mui/x-data-grid';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { Controller, useFormContext } from 'react-hook-form';
import { getExistingRow, getNewRow } from './IncludeEdit';

interface GridCellProps {
  [key: string]: any;
}

interface ICostToTravelerCellProps {
  gridIdentifier: string;
}
export const CostToTravelerCell =
  ({ ...props }: ICostToTravelerCellProps) =>
  (cellParams: GridCellParams) => {
    return <CostToTraveler cellParams={cellParams} gridIdentifier={props.gridIdentifier} />;
  };

const CostToTraveler = React.memo(function CostToTraveler(cellParams: GridCellProps) {
  const dispatch = useDispatch();
  const [CostToTraveler, setCostToTraveler] = useState(cellParams?.cellParams?.row?.costToTraveler);
  const furnitureEditRequests = useSelector(selectEditFurnitureRows);
  const utilityEditRequests = useSelector(selectEditUtilitiesRows);
  const { control } = useFormContext();
  const RowId = cellParams?.cellParams?.id;
  const isUtility = cellParams.gridIdentifier === 'Utility';
  const furnitureCostToTravelerErrors = useSelector(selectFurnitureCostToTravelerError);
  const checkAndSetEntryValue = value => {
    const pattern = /^(?:(\d{1,4})(?:\.(\d{0,2}))?|(\d{5})(?:\.(\d{0,1}))?|(\d{6}))$/;

    if (pattern.test(value)) {
      setCostToTraveler(value);
      return value;
    } else {
      setCostToTraveler(value.slice(0, -1));
      return null;
    }
  };
  const setCostToTravelerEdit = data => {
    let newRow = isUtility ? utilityEditRequests.slice(0) : furnitureEditRequests.slice(0);
    let EditRow = isUtility
      ? cellParams?.cellParams?.row?.placementUtilityId
        ? getExistingRow(utilityEditRequests, cellParams, isUtility)
        : getNewRow(utilityEditRequests, RowId)
      : cellParams?.cellParams?.row?.placementFurnitureId
      ? getExistingRow(furnitureEditRequests, cellParams, isUtility)
      : getNewRow(furnitureEditRequests, RowId);
    let dataToUpdate = checkAndSetEntryValue(data) === null ? null : Number(checkAndSetEntryValue(data));
    let updatedRow = { ...EditRow, costToTraveler: dataToUpdate };
    let editCostToTravelerIndex = isUtility
      ? utilityEditRequests?.findIndex(
          cellParams?.cellParams?.row?.placementUtilityId
            ? row => row.placementUtilityId === updatedRow.placementUtilityId
            : row => row.id === RowId,
        )
      : furnitureEditRequests?.findIndex(
          cellParams?.cellParams?.row?.placementFurnitureId
            ? row => row.placementFurnitureId === updatedRow.placementFurnitureId
            : row => row.id === RowId,
        );
    newRow[editCostToTravelerIndex] = updatedRow;
    isUtility
      ? dispatch(placementModsAction.setEditUtilitiesRows(newRow))
      : dispatch(placementModsAction.setEditFurnitureRows(newRow));
  };

  const resetCostToTraveler = () => {
    let newRow = isUtility ? utilityEditRequests?.slice(0) : furnitureEditRequests?.slice(0);
    let EditRow = isUtility
      ? cellParams?.cellParams?.row?.placementUtilityId
        ? getExistingRow(utilityEditRequests, cellParams, isUtility)
        : getNewRow(utilityEditRequests, RowId)
      : cellParams?.cellParams?.row?.placementFurnitureId
      ? getExistingRow(furnitureEditRequests, cellParams, isUtility)
      : getNewRow(furnitureEditRequests, RowId);
    setCostToTraveler('');
    let updatedRow = isUtility
      ? { ...EditRow, costToTraveler: null, billable: false }
      : { ...EditRow, costToTraveler: null, quantity: null };

    let editCostToTravelerIndex = isUtility
      ? utilityEditRequests?.findIndex(
          cellParams?.cellParams?.row?.placementUtilityId
            ? row => row.placementUtilityId === updatedRow.placementUtilityId
            : row => row.id === RowId,
        )
      : furnitureEditRequests?.findIndex(
          cellParams?.cellParams?.row?.placementFurnitureId
            ? row => row.placementFurnitureId === updatedRow.placementFurnitureId
            : row => row.id === RowId,
        );
    newRow[editCostToTravelerIndex] = updatedRow;
    isUtility
      ? dispatch(placementModsAction.setEditUtilitiesRows(newRow))
      : dispatch(placementModsAction.setEditFurnitureRows(newRow));
  };
  useEffect(() => {
    let tableData = isUtility ? utilityEditRequests : furnitureEditRequests;
    const rowData = tableData.find(value => value.id === cellParams?.cellParams?.row?.id);
    if (!rowData?.include) {
      resetCostToTraveler();
    }
  }, [cellParams?.cellParams?.row?.include]);

  useEffect(() => {
    if (cellParams?.cellParams?.error !== null && cellParams?.cellParams?.error !== undefined) {
      const val = furnitureCostToTravelerErrors?.filter(el => el.id === cellParams?.cellParams?.id);

      if (val?.length === 0) {
        const arr = furnitureCostToTravelerErrors?.length > 0 ? furnitureCostToTravelerErrors?.slice(0) : [];
        const valueObj = { costToTravel: cellParams?.cellParams?.error, id: cellParams?.cellParams?.id };
        arr.push(valueObj);
        dispatch(placementModsAction.setFurnitureCostToTravelError(arr));
      } else {
        const arr = furnitureCostToTravelerErrors?.slice(0);
        let currentObj = arr.find(val => val.id === cellParams?.cellParams?.id);
        let updatedObj = { ...currentObj, costToTravel: cellParams?.cellParams?.error };
        let editIndex = arr.findIndex(val => val.id === cellParams?.cellParams?.id);
        arr[editIndex] = updatedObj;
        dispatch(placementModsAction.setFurnitureCostToTravelError(arr));
      }
    }
  }, [cellParams?.cellParams?.error]);

  return (
    <>
      <Controller
        name={`${isUtility ? 'utilityRequests' : 'furnitureRequests'}${RowId}.costToTraveler`}
        control={control}
        render={({ field: { onChange, onBlur, value, ...rest } }) => (
          <TextField
            variant="outlined"
            disabled={!cellParams?.cellParams?.row?.include}
            size={'small'}
            style={{ width: '150px' }}
            value={cellParams?.cellParams?.row?.include ? CostToTraveler : ''}
            inputProps={{ maxLength: 255 }}
            onBlur={e => {
              setCostToTravelerEdit(e.target.value);
              onBlur();
            }}
            onChange={e => {
              if (e.target.value) {
                checkAndSetEntryValue(e.target.value);
              } else {
                setCostToTraveler(null);
              }
              onChange(e.target.value);
            }}
            {...rest}
          />
        )}
      />
    </>
  );
});
